import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { storage } from '../config/firebase';
import { userService } from './userService';
import { authService } from './authService';

class ProfileService {
  constructor() {
    this.storageRef = 'profile-pictures';
    this.maxFileSize = 5 * 1024 * 1024;
    this.allowedFileTypes = ['image/jpeg', 'image/png', 'image/webp'];
  }

  validateProfilePicture(file) {
    const errors = [];

    if (!file) {
      errors.push('Please select a file');
      return { isValid: false, errors };
    }

    if (!this.allowedFileTypes.includes(file.type)) {
      errors.push('Only JPEG, PNG, and WebP files are allowed');
    }

    if (file.size > this.maxFileSize) {
      errors.push('File size must be less than 5MB');
    }

    if (!file.type.startsWith('image/')) {
      errors.push('Please select a valid image file');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  async uploadProfilePicture(userId, file) {
    try {
      const validation = this.validateProfilePicture(file);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const fileExtension = file.name.split('.').pop();
      const fileName = `${userId}_${Date.now()}.${fileExtension}`;
      const fileRef = ref(storage, `${this.storageRef}/${fileName}`);

      console.log('Uploading profile picture...');
      
      const snapshot = await uploadBytes(fileRef, file);
      console.log('File uploaded successfully');

      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('Download URL obtained:', downloadURL);

      await userService.updateUser(userId, {
        profilePicture: downloadURL,
        profilePictureFileName: fileName,
        profilePictureUpdatedAt: new Date()
      });

      console.log('Profile picture updated in database');
      return downloadURL;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw new Error('Failed to upload profile picture');
    }
  }

  async removeProfilePicture(userId) {
    try {
      const userData = await userService.getUserById(userId);
      
      if (userData.profilePictureFileName) {
        const fileRef = ref(storage, `${this.storageRef}/${userData.profilePictureFileName}`);
        
        try {
          await deleteObject(fileRef);
          console.log('Profile picture deleted from storage');
        } catch (error) {
          console.warn('Could not delete file from storage:', error);
        }
      }

      await userService.updateUser(userId, {
        profilePicture: null,
        profilePictureFileName: null,
        profilePictureUpdatedAt: new Date()
      });

      console.log('Profile picture removed from database');
      return true;
    } catch (error) {
      console.error('Error removing profile picture:', error);
      throw new Error('Failed to remove profile picture');
    }
  }

  // UPDATED: Update profile info with fullName
  async updateProfileInfo(userId, profileData) {
    try {
      const errors = {};
      
      if (!profileData.fullName || !profileData.fullName.trim()) {
        errors.fullName = 'Full name is required';
      }
      
      if (profileData.phoneNumber) {
        const phonePattern = /^[6-9]\d{9}$/;
        const cleanedPhone = profileData.phoneNumber.replace(/\D/g, '');
        if (!phonePattern.test(cleanedPhone)) {
          errors.phoneNumber = 'Please enter a valid 10-digit phone number';
        }
      }

      if (Object.keys(errors).length > 0) {
        throw new Error(Object.values(errors)[0]);
      }

      const updateData = {
        fullName: profileData.fullName.trim(),
        phoneNumber: profileData.phoneNumber ? profileData.phoneNumber.replace(/\D/g, '') : '',
        profileUpdatedAt: new Date()
      };

      await userService.updateUser(userId, updateData);
      console.log('Profile info updated successfully');

      return updateData;
    } catch (error) {
      console.error('Error updating profile info:', error);
      throw error;
    }
  }

  // UPDATED: Get profile data with fullName
  async getProfileData(userId) {
    try {
      const userData = await userService.getUserById(userId);
      
      return {
        id: userData.id,
        fullName: userData.fullName || '',
        email: userData.email || '',
        phoneNumber: userData.phoneNumber || '',
        role: userData.role || '',
        sector: userData.sector || '',
        department: userData.department || '',
        profilePicture: userData.profilePicture || null,
        status: userData.status || '',
        createdAt: userData.createdAt,
        lastLoginAt: userData.lastLoginAt,
        profileUpdatedAt: userData.profileUpdatedAt || null
      };
    } catch (error) {
      console.error('Error getting profile data:', error);
      throw new Error('Failed to load profile data');
    }
  }

  async sendPasswordResetEmail(email) {
    try {
      await authService.resetPassword(email);
      console.log('Password reset email sent');
      return { message: 'Password reset email sent successfully' };
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  }

  getProfilePictureUrl(userData) {
    if (userData?.profilePicture) {
      return userData.profilePicture;
    }
    
    return this.generateAvatarUrl(userData);
  }

  // UPDATED: Generate avatar with fullName
  generateAvatarUrl(userData) {
    if (!userData) return null;
    
    const initials = this.getInitials(userData.fullName);
    const backgroundColor = this.getColorFromName(userData.fullName || userData.email);
    
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${backgroundColor}&color=ffffff&size=200&font-size=0.6`;
  }

  // UPDATED: Get initials from fullName
  getInitials(fullName) {
    if (!fullName) return '?';
    
    const names = fullName.trim().split(' ').filter(n => n.length > 0);
    
    if (names.length === 0) return '?';
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    
    return names[0].charAt(0).toUpperCase() + names[names.length - 1].charAt(0).toUpperCase();
  }

  getColorFromName(name) {
    if (!name) return '6B7280';
    
    const colors = [
      '3B82F6', '10B981', 'F59E0B', 'EF4444', 
      '8B5CF6', 'F97316', '06B6D4', 'EC4899', 
      '84CC16', '6366F1'
    ];
    
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  }

  canEditProfile(targetUserId, currentUser) {
    if (!currentUser) return false;
    if (currentUser.uid === targetUserId) return true;
    if (currentUser.role === 'admin') return true;
    return false;
  }

  validateProfileAccess(targetUserId, currentUser, action = 'view') {
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    const isOwnProfile = currentUser.uid === targetUserId;
    const isAdmin = currentUser.role === 'admin';

    switch (action) {
      case 'view':
        if (!isOwnProfile && !isAdmin) {
          throw new Error('Not authorized to view this profile');
        }
        break;
      
      case 'edit':
        if (!isOwnProfile && !isAdmin) {
          throw new Error('Not authorized to edit this profile');
        }
        break;
      
      case 'delete':
        if (!isAdmin) {
          throw new Error('Only administrators can delete profiles');
        }
        if (isOwnProfile) {
          throw new Error('Cannot delete your own profile');
        }
        break;
      
      default:
        throw new Error('Invalid action');
    }

    return true;
  }

  async compressImage(file, maxWidth = 400, quality = 0.8) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        const newWidth = img.width * ratio;
        const newHeight = img.height * ratio;

        canvas.width = newWidth;
        canvas.height = newHeight;

        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        
        canvas.toBlob(
          (blob) => {
            resolve(new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            }));
          },
          file.type,
          quality
        );
      };

      img.src = URL.createObjectURL(file);
    });
  }

  createImagePreview(file) {
    return URL.createObjectURL(file);
  }

  cleanupImagePreview(url) {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  getFileSizeString(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // UPDATED: Profile completion with fullName
  getProfileCompletionPercentage(userData) {
    if (!userData) return 0;
    
    const fields = [
      'fullName',
      'email',
      'sector',
      'department',
      'phoneNumber',
      'profilePicture'
    ];
    
    let completedFields = 0;
    
    fields.forEach(field => {
      if (userData[field] && userData[field].toString().trim()) {
        completedFields++;
      }
    });
    
    return Math.round((completedFields / fields.length) * 100);
  }
}

export const profileService = new ProfileService();